import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Fragment, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';

import FileCard from '../../../components/FileList/file-card';
import FileListGrid from '../../../components/FileList/file-list-grid';
import UploadForm from '../../../components/UploadForm/upload-form';
import { applicationDeleteFile, applicationSaveFile, applicationSetField } from '../../../redux/scholarship.actions';
import { EnumAppDocFormat, EnumAppDocType, EnumFileStatus, MAppTranscript, MProfile } from '../../../redux/scholarship.models';
import { selectLinkedChildProfile, selectProfilesChild, selectTranscript } from '../../../redux/scholarship.selectors';
import { ApplicationFormContainer } from '../../ApplicationsForm/applications-form.styles';

interface ApplicationsFormTranscriptProps {
  id: string;
  userId: string | undefined;
  thisSection: {
    filePath?: string;
    docFormat: EnumAppDocFormat;
    docType: EnumAppDocType;
  };
  scanning: boolean;
  dispatchDeleteFile: (
    docFormat: EnumAppDocFormat,
    docType: EnumAppDocType,
    docStatus: EnumFileStatus,
    body: Record<string, unknown>,
    id: string
  ) => void;
  dispatchSaveFile: (saveFile: MAppTranscript | Record<string, unknown>, section: EnumAppDocType) => void;
}

const ApplicationsFormTranscript = ({
  id,
  userId,
  thisSection,
  scanning,
  dispatchDeleteFile,
  dispatchSaveFile
}: ApplicationsFormTranscriptProps) => {
  const transcript = useSelector<unknown, MAppTranscript[]>(selectTranscript as (state: unknown) => MAppTranscript[]);
  const { trackPageView } = useMatomo();
  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const profilesChild = useSelector<unknown, MProfile[]>(selectProfilesChild as (state: unknown) => MProfile[]);
  const linkedChildProfile = useSelector(selectLinkedChildProfile);

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    const getChildName = () => {
      if (linkedChildProfile && profilesChild) {
        const thisProfile = profilesChild.find((item) => item.id === linkedChildProfile);
        setChildFirstName(thisProfile?.firstName || '');
        setChildLastName(thisProfile?.lastName || '');
      }
    };
    getChildName();
  }, [linkedChildProfile]);

  const handleFileDelete = (confirmation) => {
    const body = {
      confirm: confirmation,
      filepath: thisSection.filePath
    };
    dispatchDeleteFile(thisSection.docFormat, thisSection.docType, EnumFileStatus.DELETED, body, id);
  };

  const handleFile = (saveFile: MAppTranscript | Record<string, unknown>, section: EnumAppDocType): void => {
    dispatchSaveFile(saveFile, section);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <div>
      <Box pt={3} pb={3} pl={1}>
        <ApplicationFormContainer>
          <Box ml={4} mr={4} mb={3}>
            <Fragment>
              <Typography variant="h6" gutterBottom>
                School Report Card or Transcript
              </Typography>
              <Typography variant="body1" gutterBottom>
                You must submit a copy of your child's full report card or transcript for the previous school year.
              </Typography>
            </Fragment>
            <Box mt={3}>
              <UploadForm
                accept={['image/*', 'application/pdf']}
                appId={id}
                dataSource={transcript[0]}
                disabled={!!thisSection.filePath}
                section={'transcript'}
                userId={userId}
                handleFile={handleFile}
                appendIdentifier={true}
                identifier={`${childFirstName}${childLastName}`}
              />
              {thisSection.filePath &&
                (isMobile ? (
                  <FileCard files={transcript} scanning={scanning} handleFileDelete={handleFileDelete} />
                ) : (
                  <FileListGrid files={transcript} scanning={scanning} handleFileDelete={handleFileDelete} />
                ))}
            </Box>
          </Box>
        </ApplicationFormContainer>
      </Box>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchApplicationUpdate: (id: string, section: string, field: string, value: unknown): void => {
    dispatch(applicationSetField(id, section, field, value));
  },
  dispatchDeleteFile: (
    docFormat: EnumAppDocFormat,
    docType: EnumAppDocType,
    docStatus: EnumFileStatus,
    body: Record<string, unknown>,
    id: string
  ): void => {
    dispatch(applicationDeleteFile(docFormat, docType, docStatus, body, id));
  },
  dispatchSaveFile: (saveFile: MAppTranscript | Record<string, unknown>, section: EnumAppDocType): void => {
    dispatch(applicationSaveFile(saveFile, section));
  }
});
export default connect(null, mapDispatchToProps)(ApplicationsFormTranscript);
